import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';

// styling
import { Spacing, Colors } from '_styles';

// utils
import { getTaskCardStatus, getJobStatusColor, getTimeString, truncateName, convertToDate } from "_utils/functions";

// redux
import { useSelector } from "react-redux";
import { selectAirportLocations } from '_redux/slices/timeline/timeline-areas';

// icons
import { MdOutlineCheckCircle } from "react-icons/md";

const JobStatus = (props) => {

    // redux
    const airportLocations = useSelector(selectAirportLocations);

    // states
    const [jobStatusLine, setJobStatusLine] = useState("");                                             // status of job    
    const [locationColor, setLocationColor] = useState('green');                                        // checked in status
    const [showCheckInOverlay, setShowCheckInOverlay] = useState(false);                                // hover overlay state
    const [showAltPickupOverlay, setShowAltPickupOverlay] = useState(false);                            // alt pickup hover overlay state
    const [overlayPosition, setOverlayPosition] = useState({ x: 0, y: 0 });                             // overlay position
    const [elapsedTime, setElapsedTime] = useState("");                                                 // elapsed time since check-in
    const [altPickupElapsedTime, setAltPickupElapsedTime] = useState("");                               // elapsed time since alt pickup
    const [timeUntilPickup, setTimeUntilPickup] = useState("");                                         // time until pickup required

    // hover handlers for check-in overlay
    const handleMouseEnter = useCallback((e) => {
        setShowCheckInOverlay(true);
        setOverlayPosition({ x: e.clientX, y: e.clientY });
    }, []);

    const handleMouseLeave = useCallback(() => {
        setShowCheckInOverlay(false);
    }, []);

    const handleMouseMove = useCallback((e) => {
        setOverlayPosition({ x: e.clientX, y: e.clientY });
    }, []);

    // hover handlers for alt pickup overlay
    const handleAltPickupMouseEnter = useCallback((e) => {
        setShowAltPickupOverlay(true);
        setOverlayPosition({ x: e.clientX, y: e.clientY });
    }, []);

    const handleAltPickupMouseLeave = useCallback(() => {
        setShowAltPickupOverlay(false);
    }, []);

    const handleAltPickupMouseMove = useCallback((e) => {
        setOverlayPosition({ x: e.clientX, y: e.clientY });
    }, []);

    // Helper function to calculate elapsed time string
    const calculateElapsedTime = useCallback((checkInTime) => {
        if (!checkInTime) return '';

        const checkInDate = new Date(checkInTime);
        const now = new Date();
        const diffMs = now - checkInDate;

        if (diffMs <= 0) return '';

        const diffSecs = Math.floor(diffMs / 1000);
        const hours = Math.floor(diffSecs / 3600);
        const minutes = Math.floor((diffSecs % 3600) / 60);
        const seconds = diffSecs % 60;

        if (hours > 0) {
            return `${hours}h ${minutes}m ${seconds}s`;
        } else if (minutes > 0) {
            return `${minutes}m ${seconds}s`;
        } else {
            return `${seconds}s`;
        }
    }, []);

    // Helper function to calculate time until pickup (countdown)
    const calculateTimeUntilPickup = useCallback((pickUpTime) => {
        if (!pickUpTime) return '';

        const pickUpDate = new Date(convertToDate(pickUpTime));
        const now = new Date();
        const diffMs = pickUpDate - now;

        // If pickup time has passed, show "Overdue"
        if (diffMs <= 0) {
            const overdueSecs = Math.floor(Math.abs(diffMs) / 1000);
            const hours = Math.floor(overdueSecs / 3600);
            const minutes = Math.floor((overdueSecs % 3600) / 60);
            if (hours > 0) {
                return `Overdue by ${hours}h ${minutes}m`;
            } else if (minutes > 0) {
                return `Overdue by ${minutes}m`;
            } else {
                return `Overdue`;
            }
        }

        const diffSecs = Math.floor(diffMs / 1000);
        const hours = Math.floor(diffSecs / 3600);
        const minutes = Math.floor((diffSecs % 3600) / 60);

        if (hours > 0) {
            return `${hours}h ${minutes}m`;
        } else if (minutes > 0) {
            return `${minutes}m`;
        } else {
            return `< 1m`;
        }
    }, []);

    // Update elapsed time every second while check-in overlay is visible
    useEffect(() => {
        if (!showCheckInOverlay) return;

        // const paxInfo = props?.job?.originalBookingDoc?.paxPositionInfo || props?.job?.paxPositionInfo;
        // const arrivalCheckIn = props?.job?.originalBookingDoc?.arrivalPaxCheckIn;
        // const checkInTime = paxInfo?.time || arrivalCheckIn?.time;

        let checkInTime = '';
        if (props?.userIata == props?.job?.flightInformation?.departureAirport || props?.userIata == props?.job?.flightConfirmationDetails?.origin) {
            if (props?.job?.paxPositionInfo?.activity === 'Checked-in') {
                checkInTime = props?.job?.paxPositionInfo?.time;
            } else {
                const lastCheckedIn = props?.job?.paxPositionHistory?.find(history => history?.activity === 'Checked-in');
                checkInTime = lastCheckedIn ? lastCheckedIn?.time : null;
            }
        } else {
            checkInTime = (props?.job?.originalBookingDoc?.arrivalPaxCheckIn?.time || props?.job?.arrivalPaxCheckIn?.time)
                ? props?.job?.originalBookingDoc?.arrivalPaxCheckIn?.time || props?.job?.arrivalPaxCheckIn?.time
                : null;
        }
        if (!checkInTime) return;

        // Update immediately
        setElapsedTime(calculateElapsedTime(checkInTime));

        // Then update every second
        const interval = setInterval(() => {
            setElapsedTime(calculateElapsedTime(checkInTime));
        }, 1000);

        return () => clearInterval(interval);
    }, [showCheckInOverlay, props?.job, props?.userIata, calculateElapsedTime]);

    // Update elapsed time and time until pickup every second while alt pickup overlay is visible
    // Use paxPositionInfo.time (when dropped off) for waiting time calculation
    useEffect(() => {
        if (!showAltPickupOverlay) return;

        // drop-off ALT tasks store a Firestore Timestamp here, gate relocations an ISO string
        const droppedOffTime = convertToDate(props?.job?.paxPositionInfo?.time);
        const pickUpTime = props?.job?.pickUpTime;

        // Update immediately
        if (droppedOffTime) {
            setAltPickupElapsedTime(calculateElapsedTime(droppedOffTime));
        }
        if (pickUpTime) {
            setTimeUntilPickup(calculateTimeUntilPickup(pickUpTime));
        }

        // Then update every second
        const interval = setInterval(() => {
            if (droppedOffTime) {
                setAltPickupElapsedTime(calculateElapsedTime(droppedOffTime));
            }
            if (pickUpTime) {
                setTimeUntilPickup(calculateTimeUntilPickup(pickUpTime));
            }
        }, 1000);

        return () => clearInterval(interval);
    }, [showAltPickupOverlay, props?.job, calculateElapsedTime, calculateTimeUntilPickup]);

    useEffect(() => {

        // check if the job has an rejectedPax change it's status to Rejected
        const isRejectedArrJourney = props?.job?.originalBookingDoc?.paxRejection?.find((paxRejection) => paxRejection?.type === "arrivalJourney");
        const isRejectedDepJourney = props?.job?.paxRejection?.find((paxRejection) => paxRejection?.type === "preBoardJourney");
        const isRejectedArrPax = props?.job?.originalBookingDoc?.paxRejection?.find((paxRejection) => paxRejection?.type === "arrivalPax");
        const isRejectedDepPax = props?.job?.paxRejection?.find((paxRejection) => paxRejection?.type === "departurePax");
        if (props?.job?.rejectedPax == true || props?.job?.originalBookingDoc?.rejectedPax == true || props?.job?.rejectedALTPax == true || isRejectedArrJourney || isRejectedDepJourney || isRejectedArrPax || isRejectedDepPax) setJobStatusLine("Rejected");

        else if (props.booking) {

            // its a booking so we need to check the status using the pax location
            let status = props?.job?.paxPositionInfo?.paxStatus || props?.job?.paxPositionInfo?.activity
            status = String(status).charAt(0).toUpperCase() + String(status).slice(1);

            // shorted to 6 letters
            status = status.length > 4 ? status.substring(0, 4) + "..." : status;

            setJobStatusLine(status);
        }
        // held by the break queue: not on the agent's device until their break is finished
        else if (props.job?.queued) setJobStatusLine("Queued");
        else {
            // For unassigned gate relocations, show empty status
            if (props?.job?.isGateRelocation && !props?.job?.assignedTime) {
                setJobStatusLine("");
            } else {
                let cardStatus = getTaskCardStatus(props?.job, props?.fromLocationPanel)
                setJobStatusLine(cardStatus?.status)
            }
        }

        // check if the job has a location and set the color accordingly for checked in
        if (props?.job?.paxPositionInfo?.activity == 'Checked-in' && !props?.job?.assignedTime) {
            const locationId = props?.job?.paxPositionInfo?.locationId;


            // use location list to get location color
            const location = airportLocations?.length > 0 ? airportLocations?.find((location) => location['_id'] == locationId) : null;
            const color = location?.color || 'green';
            setLocationColor(color);

        }

    }, [props?.job, props?.booking, airportLocations]);

    // check-in details for overlay
    const checkInDetails = useMemo(() => {
        const paxInfo = props?.job?.originalBookingDoc?.paxPositionInfo || props?.job?.paxPositionInfo;
        const arrivalCheckIn = props?.job?.originalBookingDoc?.arrivalPaxCheckIn;

        if (paxInfo?.activity === "Checked-in" || arrivalCheckIn?.locationId || paxInfo?.activity === "Assigned") {
            // const checkInTime = paxInfo?.time || arrivalCheckIn?.time;
            let checkInTime = '';
            if (props?.userIata == props?.job?.flightInformation?.departureAirport || props?.userIata == props?.job?.flightConfirmationDetails?.origin) {
                if (props?.job?.paxPositionInfo?.activity === 'Checked-in') {
                    checkInTime = props?.job?.paxPositionInfo?.time;
                } else {
                    const lastCheckedIn = props?.job?.paxPositionHistory?.find(history => history?.activity === 'Checked-in');
                    checkInTime = lastCheckedIn ? lastCheckedIn?.time : null;
                }
            } else {
                checkInTime = (props?.job?.originalBookingDoc?.arrivalPaxCheckIn?.time || props?.job?.arrivalPaxCheckIn?.time)
                    ? props?.job?.originalBookingDoc?.arrivalPaxCheckIn?.time || props?.job?.arrivalPaxCheckIn?.time
                    : null;
            }

            if (!checkInTime) return null;

            return {
                location: paxInfo?.locationTitle || arrivalCheckIn?.locationTitle || 'Unknown',
                time: checkInTime,
                locationId: paxInfo?.locationId || arrivalCheckIn?.locationId
            };
        }
        return null;
    }, [props?.job, props?.userIata]);

    // check if this is a checked-in status
    const isCheckedIn = props?.job?.paxPositionInfo?.activity == "Checked-in" ||
        props?.job?.originalBookingDoc?.paxPositionInfo?.activity == "Checked-in" ||
        props?.job?.originalBookingDoc?.arrivalPaxCheckIn?.locationId;

    // check if this is an unassigned alt pickup
    const isUnassignedAltPickup = props?.job?.isALTPickUp && props?.job?.assignedTime == undefined && !props?.job?.rejectedPax;

    return (
        <div
            style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: Spacing.SCALE_2, backgroundColor: 'white', borderRadius: 2, paddingLeft: Spacing.SCALE_2, paddingRight: Spacing.SCALE_2, cursor: (isCheckedIn && !props?.job?.assignedTime) || isUnassignedAltPickup ? 'pointer' : 'default' }}
            data-tippy-content="Assistance Task Status"
            onMouseEnter={(isCheckedIn && !props?.job?.assignedTime) || props?.job.paxPositionInfo?.activity === "Assigned" ? handleMouseEnter : isUnassignedAltPickup ? handleAltPickupMouseEnter : undefined}
            onMouseLeave={(isCheckedIn && !props?.job?.assignedTime) || props?.job.paxPositionInfo?.activity === "Assigned" ? handleMouseLeave : isUnassignedAltPickup ? handleAltPickupMouseLeave : undefined}
            onMouseMove={(isCheckedIn && !props?.job?.assignedTime) || props?.job.paxPositionInfo?.activity === "Assigned" ? handleMouseMove : isUnassignedAltPickup ? handleAltPickupMouseMove : undefined}
        >

            {/* check in icon or status circle - hide for unassigned alt pickups */}
            {!isUnassignedAltPickup && (
                <div style={{
                    display: 'flex', alignItems: 'center'
                }}>
                    {(props?.job?.paxPositionInfo?.activity == "Checked-in" && !props?.job?.assignedTime) || (props?.job?.originalBookingDoc?.paxPositionInfo?.activity == "Checked-in" && !props?.job?.assignedTime) || (props?.job?.originalBookingDoc?.arrivalPaxCheckIn?.locationId && !props?.job?.assignedTime) ?
                        <>
                            {props?.userIata === "JER" && <div class="glowing-circle"></div>}
                            <MdOutlineCheckCircle size={12} color={locationColor} />
                        </>
                        :
                        props?.job?.assignedTime || (props?.job?.paxPositionInfo && props?.job?.paxPositionInfo?.activity !== "Check-out") ?
                            <div style={{ width: Spacing.SCALE_6, height: Spacing.SCALE_6, borderRadius: 25, backgroundColor: getJobStatusColor(props?.job) }} />
                            : null
                    }
                </div>
            )}

            {/* status text */}
            <div style={{ flex: 8, justifyContent: 'center', alignItems: 'center' }}>
                <p style={{ overflowX: 'hidden', margin: "0px", textAlign: "center", fontSize: 8.5, color: props?.job?.paxPositionInfo?.activity == "Checked-in" || props?.job?.originalBookingDoc?.arrivalPaxCheckIn?.locationId ? locationColor : getJobStatusColor(props?.job), fontWeight: "700", whiteSpace: 'nowrap' }}>
                    {isCheckedIn
                        ? getTimeString(props?.job?.originalBookingDoc?.paxPositionInfo?.time || props?.job?.paxPositionInfo?.time || props?.job?.originalBookingDoc?.arrivalPaxCheckIn?.time)
                        : isUnassignedAltPickup && props?.job?.pickUpLocation?.name
                            ? truncateName(props?.job?.pickUpLocation?.name)
                            : jobStatusLine}
                </p>
            </div>

            {/* Check-in details overlay */}
            {showCheckInOverlay && checkInDetails && createPortal(
                <div
                    style={{
                        position: 'fixed',
                        left: overlayPosition.x + 10,
                        top: overlayPosition.y + 10,
                        backgroundColor: 'white',
                        border: `1px solid ${Colors.GRAY_LIGHT}`,
                        borderRadius: 8,
                        padding: Spacing.SCALE_8,
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                        zIndex: 2147483647,
                        maxWidth: 250,
                        fontSize: 12,
                        color: Colors.TEXT_DARK,
                        pointerEvents: 'none',
                    }}
                >
                    <div style={{ fontWeight: 'bold', marginBottom: 4, color: locationColor }}>
                        Check-in Details
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        <div><strong>Location:</strong> {checkInDetails.location}</div>
                        <div><strong>Time:</strong> {getTimeString(checkInDetails.time)}</div>
                        {elapsedTime && <div><strong>Waiting:</strong> {elapsedTime}</div>}
                    </div>
                </div>,
                document.body
            )}

            {/* Alt Pickup details overlay */}
            {showAltPickupOverlay && isUnassignedAltPickup && createPortal(
                <div
                    style={{
                        position: 'fixed',
                        left: overlayPosition.x + 10,
                        top: overlayPosition.y + 10,
                        backgroundColor: 'white',
                        border: `1px solid ${Colors.GRAY_LIGHT}`,
                        borderRadius: 8,
                        padding: Spacing.SCALE_8,
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                        zIndex: 2147483647,
                        maxWidth: 250,
                        fontSize: 12,
                        color: Colors.TEXT_DARK,
                        pointerEvents: 'none',
                    }}
                >
                    <div style={{ fontWeight: 'bold', marginBottom: 4, color: '#8B5CF6' }}>
                        {props?.job?.isGateRelocation ? 'Gate Relocation Details' : 'Alt Pickup Details'}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        {props?.job?.isGateRelocation && props?.job?.relocationReason && (
                            <div><strong>Reason:</strong> {props?.job?.relocationReason}</div>
                        )}
                        <div><strong>Pick Up Location:</strong> {props?.job?.pickUpLocation?.name || 'Unknown'}</div>
                        <div><strong>Drop Off Location:</strong> {props?.job?.dropOffLocation?.name || 'Unknown'}</div>
                        {props?.job?.pickUpTime && <div><strong>Pick Up Time:</strong> {convertToDate(props?.job?.pickUpTime) ? getTimeString(convertToDate(props?.job?.pickUpTime)) : props?.job?.pickUpTime}</div>}
                        {convertToDate(props?.job?.paxPositionInfo?.time) && <div><strong>Dropped Off:</strong> {getTimeString(convertToDate(props?.job?.paxPositionInfo?.time))}</div>}
                        {altPickupElapsedTime && <div><strong>Waiting:</strong> {altPickupElapsedTime}</div>}
                        {timeUntilPickup && <div><strong>Pickup Required:</strong> <span style={{ color: timeUntilPickup.includes('Overdue') ? '#dc2626' : '#16a34a' }}>{timeUntilPickup}</span></div>}
                    </div>
                </div>,
                document.body
            )}

        </div>
    );
};

export default React.memo(JobStatus, (prev, next) => {
    return (
        prev.job === next.job &&
        prev.booking === next.booking &&
        prev.job?.paxPositionInfo?.time === next.job?.paxPositionInfo?.time &&
        prev.job?.originalBookingDoc?.paxPositionInfo?.time === next.job?.originalBookingDoc?.paxPositionInfo?.time
    );
});
